import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BookingPushStatusBadge } from './BookingPushStatusBadge';
import type { BookingPushLog } from '@/types/api';

interface BookingPushLogsListProps {
  logs: BookingPushLog[];
  isLoading?: boolean;
}

export function BookingPushLogsList({ logs, isLoading }: BookingPushLogsListProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="h-12 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No booking push logs found.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Booking ID</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Partner Account</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>External Ref</TableHead>
          <TableHead>Retries</TableHead>
          <TableHead>Created</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {logs.map((log) => (
          <TableRow key={log.id} data-testid={`booking-push-log-${log.id}`}>
            <TableCell className="font-mono text-sm">{log.bookingId}</TableCell>
            <TableCell>
              <Link
                to={`/synced-locations/${log.syncedLocationId}`}
                className="text-primary hover:underline"
              >
                {log.syncedLocationName}
              </Link>
            </TableCell>
            <TableCell>{log.partnerAccountName}</TableCell>
            <TableCell>
              <BookingPushStatusBadge status={log.status} />
            </TableCell>
            <TableCell className="font-mono text-sm">
              {log.externalRef ?? '-'}
            </TableCell>
            <TableCell>
              {log.retryCount > 0 && (
                <span className="text-muted-foreground">{log.retryCount}</span>
              )}
              {log.retryCount === 0 && '-'}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {new Date(log.createdAt).toLocaleString()}
            </TableCell>
            <TableCell>
              {log.errorMessage && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{log.errorMessage}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
